import {
  computeCrushLine,
  computeScaleMax,
  formatRoas,
  getRoasZone,
  roasToPercent,
  type RoasZone,
} from "./roasScale";

const ZONE_BAR_COLORS: Record<RoasZone, string> = {
  below: "bg-[#F9DEDE]",
  profitable: "bg-[#D4EDDA]",
  "well-above": "bg-[#8FCC9B]",
};

const ZONE_LABEL_COLORS: Record<RoasZone, string> = {
  below: "text-[#C44545]",
  profitable: "text-[#2D7A45]",
  "well-above": "text-[#1F5C32]",
};

const ZONE_PILL_COLORS: Record<RoasZone, string> = {
  below: "bg-[#F9DEDE] text-[#C44545] border-[#E8A8A8]",
  profitable: "bg-[#D4EDDA] text-[#2D7A45] border-[#9FD4AE]",
  "well-above": "bg-[#8FCC9B] text-[#1F5C32] border-[#6BB87D]",
};

const ZONE_DOT_COLORS: Record<RoasZone, string> = {
  below: "bg-[#C44545]",
  profitable: "bg-[#2D7A45]",
  "well-above": "bg-[#1F5C32]",
};

const BAR_HEIGHT_CLASS = "h-8";

export const RoasComparisonBar: React.FC<{
  breakevenRoas: number;
  yourRoas: number | null;
}> = ({ breakevenRoas, yourRoas }) => {
  const crushLine = computeCrushLine(breakevenRoas);
  const scaleMax = computeScaleMax(yourRoas, breakevenRoas);

  const breakevenPct = roasToPercent(breakevenRoas, scaleMax);
  const crushPct = roasToPercent(crushLine, scaleMax);
  const yourPct = yourRoas === null ? null : roasToPercent(yourRoas, scaleMax);

  const yourZone =
    yourRoas === null ? null : getRoasZone(yourRoas, breakevenRoas, crushLine);

  return (
    <div className="flex flex-col gap-2 pt-2">
      <div className="relative w-full">
        {yourRoas !== null && yourPct !== null && yourZone !== null ? (
          <div
            className="absolute bottom-full z-30 mb-2 -translate-x-1/2"
            style={{ left: `${yourPct}%` }}
          >
            <span
              className={`type-fs-label-medium inline-flex rounded-full border px-2 py-0.5 ${ZONE_PILL_COLORS[yourZone]}`}
            >
              You
            </span>
          </div>
        ) : null}

        <div className={`relative w-full ${BAR_HEIGHT_CLASS}`}>
          <div
            className={`absolute inset-0 flex overflow-hidden rounded-full ${BAR_HEIGHT_CLASS}`}
          >
            <div
              className={`flex items-center justify-center ${ZONE_BAR_COLORS.below}`}
              style={{ width: `${breakevenPct}%` }}
            >
              <span
                className={`type-fs-label-medium tracking-[0.04em] uppercase ${ZONE_LABEL_COLORS.below}`}
              >
                Below
              </span>
            </div>
            <div
              className={`flex items-center justify-center ${ZONE_BAR_COLORS.profitable}`}
              style={{ width: `${crushPct - breakevenPct}%` }}
            >
              <span
                className={`type-fs-label-medium tracking-[0.04em] uppercase ${ZONE_LABEL_COLORS.profitable}`}
              >
                Profitable
              </span>
            </div>
            <div
              className={`flex flex-1 items-center justify-center ${ZONE_BAR_COLORS["well-above"]}`}
            >
              <span
                className={`type-fs-label-medium tracking-[0.04em] uppercase ${ZONE_LABEL_COLORS["well-above"]}`}
              >
                Well above
              </span>
            </div>
          </div>

          <div
            className="pointer-events-none absolute top-1/2 z-30 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center"
            style={{ left: `${breakevenPct}%` }}
            aria-hidden
          >
            <div className="h-0 w-0 border-x-[5px] border-t-[6px] border-x-transparent border-t-[#333333]" />
            <div className="h-9 w-[3px] rounded-full bg-[#333333] shadow-[0_0_0_2px_#ffffff,0_1px_3px_rgba(0,0,0,0.25)]" />
            <div className="h-0 w-0 border-x-[5px] border-b-[6px] border-x-transparent border-b-[#333333]" />
          </div>

          {yourPct !== null && yourZone !== null ? (
            <div
              className={`absolute top-1/2 z-20 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-(--slate-color-grey-100) shadow-sm ${ZONE_DOT_COLORS[yourZone]}`}
              style={{ left: `${yourPct}%` }}
              aria-hidden
            />
          ) : null}
        </div>
      </div>

      <div className="relative h-10">
        <div
          className="text-fs-text-primary absolute -translate-x-1/2 text-center"
          style={{ left: `${breakevenPct}%`, top: 0 }}
        >
          <div className="type-fs-label-medium text-fs-text-subdued tracking-[0.08em] uppercase">
            Breakeven
          </div>
          <div className="type-fs-label-medium">
            {formatRoas(breakevenRoas)}
          </div>
        </div>
      </div>
    </div>
  );
};
